import "../../styles/header.scss";

import { ThemeMode } from "@amzn/stencil-react-components/context";
import { Row, Spacer } from "@amzn/stencil-react-components/layout";
import {
  PageHeader,
  PageHeaderList,
} from "@amzn/stencil-react-components/page";
import { ToggleSwitch } from "@amzn/stencil-react-components/toggle-switch";
import IconDarkModeExtraSmall from "@amzn/stencil-react-icons/icons/icon-dark-mode-extra-small";
import IconLightModeExtraSmall from "@amzn/stencil-react-icons/icons/icon-light-mode-extra-small";
import * as React from "react";

import { GetSupportHeaderLink } from "@/components/GetSupport";
import { LanguageSelector } from "@/components/LanguageSelector";
import { useThemeMode } from "@/stores/contexts/ThemeModeContext";

import Day1Logo from "../../assets/icons/day1logo.svg";
import Day1LogoDark from "../../assets/icons/day1logo-dark.svg";

/**
 * The site header displayed on the Day1 onboarding page and the landing page.
 *
 * @param showGetSupport Whether to render the "Get support" link. Defaults to
 * true. The unauthenticated landing page passes false: its support flyouts are
 * not mounted, and support is reached through the "I need help" flow instead.
 */
export default function Header({
  showGetSupport = true,
}: {
  showGetSupport?: boolean;
} = {}): JSX.Element {
  const { themeMode, toggleThemeMode } = useThemeMode();

  const headerItems = [
    <LanguageSelector key={1} />,
    ...(showGetSupport ? [<GetSupportHeaderLink key={2} />] : []),
  ];

  headerItems.unshift(
    <Row key="dark-mode-toggle" alignItems="center">
      <ToggleSwitch
        aria-label={
          themeMode === ThemeMode.Dark
            ? "Switch to light mode"
            : "Switch to dark mode"
        }
        title="Toggle dark mode"
        iconChecked={IconDarkModeExtraSmall}
        iconUnchecked={IconLightModeExtraSmall}
        checked={themeMode === ThemeMode.Dark}
        onChange={toggleThemeMode}
      />
    </Row>
  );

  return (
    <PageHeader maxWidth="full" hasShadow dataTestId="ezoHeader">
      <nav id="navbar" aria-label="Site">
        <Row alignItems="center" height="100%" width="100%">
          <img
            src={themeMode === ThemeMode.Dark ? Day1LogoDark : Day1Logo}
            alt="Day 1 Wizard icon"
          />
          <Spacer flex={1} />
          <PageHeaderList items={headerItems} />
        </Row>
      </nav>
    </PageHeader>
  );
}
